<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import EmailVerificationBanner from '@/components/layout/EmailVerificationBanner.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseButton from '@/components/ui/BaseButton.vue'

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed'

const sidebarCollapsed = ref(localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true')
const mobileMenuOpen = ref(false)
const isMobile = ref(false)

const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 1024 // lg breakpoint
  if (!isMobile.value) {
    mobileMenuOpen.value = false
  }
}

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed.value))
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

// Persist sidebar collapsed state
watch(sidebarCollapsed, (newValue) => {
  localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newValue))
})

onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', checkScreenSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize)
})
</script>

<template>
  <div class="tw-flex tw-h-screen tw-overflow-hidden tw-bg-surface-50" data-testid="app-layout">
    <!-- Desktop Sidebar -->
    <Transition
      enter-active-class="tw-transition-all tw-duration-300 tw-ease-out"
      enter-from-class="tw-opacity-0 -tw-translate-x-4"
      enter-to-class="tw-opacity-100 tw-translate-x-0"
      leave-active-class="tw-transition-all tw-duration-200 tw-ease-in"
      leave-from-class="tw-opacity-100 tw-translate-x-0"
      leave-to-class="tw-opacity-0 -tw-translate-x-4"
    >
      <AppSidebar
        v-if="!isMobile"
        :collapsed="sidebarCollapsed"
        class="tw-hidden lg:tw-flex"
        @toggle="toggleSidebar"
      />
    </Transition>

    <!-- Mobile Sidebar (Drawer) -->
    <Transition
      enter-active-class="tw-transition-transform tw-duration-300 tw-ease-out"
      enter-from-class="-tw-translate-x-full"
      enter-to-class="tw-translate-x-0"
      leave-active-class="tw-transition-transform tw-duration-200 tw-ease-in"
      leave-from-class="tw-translate-x-0"
      leave-to-class="-tw-translate-x-full"
    >
      <AppSidebar
        v-if="mobileMenuOpen && isMobile"
        :mobile="true"
        @close="closeMobileMenu"
      />
    </Transition>

    <!-- Main content area -->
    <div class="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden">
      <!-- Mobile header with menu toggle -->
      <header
        v-if="isMobile"
        class="tw-bg-surface-0 tw-border-b tw-border-surface-200 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-4 lg:tw-hidden"
        data-testid="mobile-header"
      >
        <BaseButton
          type="button"
          variant="bare"
          class="tw-p-2 tw-rounded-md hover:tw-bg-surface-100 tw-transition-colors"
          aria-label="Toggle mobile menu"
          data-testid="mobile-menu-button"
          @click="toggleMobileMenu"
        >
          <AppIcon name="solar:hamburger-menu-bold" size="xl" class="tw-text-surface-700" />
        </BaseButton>
      </header>

      <main class="tw-flex-1 tw-overflow-y-auto tw-bg-surface-50">
        <div class="tw-px-4 sm:tw-px-6 tw-py-6">
          <EmailVerificationBanner />
          <Transition
            mode="out-in"
            enter-active-class="tw-transition-all tw-duration-200"
            enter-from-class="tw-opacity-0 tw-translate-y-4"
            enter-to-class="tw-opacity-100 tw-translate-y-0"
            leave-active-class="tw-transition-all tw-duration-150"
            leave-from-class="tw-opacity-100"
            leave-to-class="tw-opacity-0"
          >
            <slot />
          </Transition>
        </div>
      </main>
    </div>
  </div>
</template>
